import * as THREE from "three/webgpu";
import { getSceneObjectWorldTransform } from "../app/collectionGraph.ts";
import type { Collection } from "../app/storeTypes.ts";
import { FaceGrid, type SurfaceHitResult } from "../wasm/shrinkwrap.ts";
import { buildSharedVertexIndices } from "./loopExpand.ts";
import type { SceneObject, SharedTriangleIndices } from "./types.ts";
import { isCurveObject, isLightObject, isMannequinObject } from "./types.ts";

export interface SceneSurfaceHit {
  objectId: string;
  worldPosition: [number, number, number];
  normal: [number, number, number];
  distSq?: number;
}

interface SceneSurfaceGeometry {
  objectId: string;
  positions: Float32Array;
  indices: SharedTriangleIndices;
}

export interface SceneSurfaceRaycastContext {
  meshes: THREE.Mesh[];
}

export interface SceneSurfaceProjectContext {
  surfaces: Array<{ objectId: string; grid: FaceGrid }>;
}

const _raycaster = new THREE.Raycaster();
const _scratchMatrix = new THREE.Matrix4();
const _scratchVec = new THREE.Vector3();
const _identityMatrix = new THREE.Matrix4();

function composeSceneSurfaceMatrix(
  sceneObjects: ReadonlyMap<string, SceneObject>,
  collections: ReadonlyMap<string, Collection>,
  objectId: string,
  obj: SceneObject,
): THREE.Matrix4 {
  const worldTransform = getSceneObjectWorldTransform(
    sceneObjects,
    collections,
    objectId,
  ) ?? {
    position: obj.position,
    rotation: obj.rotation,
    scale: obj.scale,
  };
  _scratchMatrix.compose(
    new THREE.Vector3(...worldTransform.position),
    new THREE.Quaternion().setFromEuler(
      new THREE.Euler(...worldTransform.rotation),
    ),
    new THREE.Vector3(...worldTransform.scale),
  );
  if (obj.originOffset) {
    _scratchMatrix.multiply(
      new THREE.Matrix4().makeTranslation(...obj.originOffset),
    );
  }
  return _scratchMatrix;
}

function transformPositionsWorld(
  positions: Float32Array,
  matrix: THREE.Matrix4,
): Float32Array {
  const out = new Float32Array(positions.length);
  const elements = matrix.elements;
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i];
    const y = positions[i + 1];
    const z = positions[i + 2];
    out[i] = elements[0] * x + elements[4] * y + elements[8] * z + elements[12];
    out[i + 1] =
      elements[1] * x + elements[5] * y + elements[9] * z + elements[13];
    out[i + 2] =
      elements[2] * x + elements[6] * y + elements[10] * z + elements[14];
  }
  return out;
}

export function buildSceneSurfaceGeometry(
  excludeIds: ReadonlySet<string>,
  sceneObjects: ReadonlyMap<string, SceneObject>,
  collections: ReadonlyMap<string, Collection>,
  visibility?: ReadonlyMap<string, boolean>,
): SceneSurfaceGeometry[] {
  const surfaces: SceneSurfaceGeometry[] = [];

  for (const [objectId, obj] of sceneObjects) {
    if (excludeIds.has(objectId)) continue;
    if ((visibility?.get(objectId) ?? true) === false) continue;
    if (isLightObject(obj) || isCurveObject(obj) || isMannequinObject(obj))
      continue;
    if (obj.vertices.length === 0 || obj.indices.length === 0 || !obj.topology)
      continue;

    const matrix = composeSceneSurfaceMatrix(
      sceneObjects,
      collections,
      objectId,
      obj,
    );
    const sharedIndices = buildSharedVertexIndices(obj.indices, obj.topology);
    surfaces.push({
      objectId,
      positions: transformPositionsWorld(obj.vertices, matrix),
      indices: sharedIndices,
    });
  }

  return surfaces;
}

export function createSceneSurfaceRaycastContext(
  excludeIds: ReadonlySet<string>,
  sceneObjects: ReadonlyMap<string, SceneObject>,
  collections: ReadonlyMap<string, Collection>,
  visibility?: ReadonlyMap<string, boolean>,
): SceneSurfaceRaycastContext {
  const geometries = buildSceneSurfaceGeometry(
    excludeIds,
    sceneObjects,
    collections,
    visibility,
  );
  const meshes = geometries.map((surface) => {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(surface.positions, 3),
    );
    geometry.setIndex(new THREE.BufferAttribute(surface.indices, 1));

    const mesh = new THREE.Mesh(geometry);
    mesh.matrixAutoUpdate = false;
    mesh.matrix.copy(_identityMatrix);
    mesh.matrixWorld.copy(_identityMatrix);
    mesh.userData.objectId = surface.objectId;
    return mesh;
  });

  return { meshes };
}

export function disposeSceneSurfaceRaycastContext(
  context: SceneSurfaceRaycastContext,
): void {
  for (const mesh of context.meshes) {
    mesh.geometry.dispose();
  }
}

export function raycastSceneSurface(
  context: SceneSurfaceRaycastContext,
  camera: THREE.Camera,
  ndcX: number,
  ndcY: number,
): SceneSurfaceHit | null {
  if (context.meshes.length === 0) return null;

  camera.updateMatrixWorld();
  _raycaster.setFromCamera({ x: ndcX, y: ndcY } as THREE.Vector2, camera);
  const hit = _raycaster.intersectObjects(context.meshes, false)[0];
  if (!hit) return null;

  const objectId = String(hit.object.userData.objectId ?? "");
  const normal = hit.face?.normal ?? _scratchVec.set(0, 0, 1);
  return {
    objectId,
    worldPosition: [hit.point.x, hit.point.y, hit.point.z],
    normal: [normal.x, normal.y, normal.z],
  };
}

export function projectWorldPointToSceneSurface(
  context: SceneSurfaceRaycastContext,
  camera: THREE.Camera,
  worldPosition: readonly [number, number, number],
): SceneSurfaceHit | null {
  _scratchVec
    .set(worldPosition[0], worldPosition[1], worldPosition[2])
    .project(camera);
  if (_scratchVec.z < -1 || _scratchVec.z > 1) return null;
  return raycastSceneSurface(context, camera, _scratchVec.x, _scratchVec.y);
}

export function createSceneSurfaceProjectContext(
  excludeIds: ReadonlySet<string>,
  sceneObjects: ReadonlyMap<string, SceneObject>,
  collections: ReadonlyMap<string, Collection>,
  visibility?: ReadonlyMap<string, boolean>,
): SceneSurfaceProjectContext {
  return {
    surfaces: buildSceneSurfaceGeometry(
      excludeIds,
      sceneObjects,
      collections,
      visibility,
    ).map((surface) => ({
      objectId: surface.objectId,
      grid: new FaceGrid(surface.positions, surface.indices),
    })),
  };
}

/** Release the WASM-backed `FaceGrid` for every surface in the context. */
export function disposeSceneSurfaceProjectContext(
  context: SceneSurfaceProjectContext,
): void {
  for (const surface of context.surfaces) {
    surface.grid.dispose();
  }
}

function toSceneSurfaceHit(
  objectId: string,
  hit: SurfaceHitResult,
): SceneSurfaceHit {
  return {
    objectId,
    worldPosition: [hit.x, hit.y, hit.z],
    normal: [hit.nx, hit.ny, hit.nz],
    distSq: hit.distSq,
  };
}

export function queryNearestSceneSurface(
  context: SceneSurfaceProjectContext,
  worldPosition: readonly [number, number, number],
): SceneSurfaceHit | null {
  let best: SceneSurfaceHit | null = null;

  for (const surface of context.surfaces) {
    const hit = surface.grid.queryNearestSurface(
      worldPosition[0],
      worldPosition[1],
      worldPosition[2],
    );
    if (!hit) continue;
    if (!best || hit.distSq < (best.distSq ?? Infinity)) {
      best = toSceneSurfaceHit(surface.objectId, hit);
    }
  }

  return best;
}
